import React from 'react'
import _ from 'lodash'
import Router from 'next/router'
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects'

import mixpanel, { EVENTS } from 'lib/mixpanel'
import SuccessfulCompletionModal from 'page-modules/mood-meter/plot-journey/successful-completion-modal'
import lang from 'lang'
import buildService, { NoParams } from 'common/utils/build-service'
import { urls } from 'common/routes'
import { changeModal } from 'services/actions'
import { validateField, validateFields } from 'common/validator'
import { AppState } from 'common/store/reducer'

import {
  addGroupFormFieldError,
  addGroupFormFieldsErrors,
  clearGroupFormFieldError,
  LOAD_MOOD_METER_GROUP_PAGE,
  resetMoodMeterGroupFormData,
  START_MOOD_METER_GROUP_PLOT,
  SUBMIT_GROUP_MOOD_METER_PLOT,
  SUBMIT_PERSONAL_MOOD_METER_PLOT,
  VALIDATE_MOOD_METER_GROUP_FORM_FIELD,
} from './actions'
import groupPlotsFilterSaga from '../group-plots-lists/filter/saga'
import { reloadGroupPlotsAfterDelete } from '../group-plots-lists/filter/actions'
import { postgrestEqFilter } from '../group-plots-lists/filter/postgrest'
import { SHARED_IN_OPTIONS } from './group-form-reducer'
import { getMoodMeterGroupByShortCodeService } from './mood-meter-service'
import {
  DeleteMoodParams,
  GetMoodMeterGroupsParams,
  GetMoodsParams,
  Mood,
  MoodMeterGroup,
  PatchMoodMeterGroupParams,
  PlotFormData,
  StartMoodMeterGroupPlotPayload,
} from './types'

export const getMoodsService = buildService<GetMoodsParams, Mood, unknown, Mood>({
  type: 'GET',
  constantsPrefix: 'MOODS',
  isArray: true,
  http: {
    createApiUrlSuffix: ({ id, dates }) => {
      if (dates) {
        const moodsUrl = `/moods?mood_meter_group_id=eq.${id}&select=*,emotion_words(*)`
        return `${moodsUrl}&created_at=gte.${dates.from}&created_at=lte.${dates.to}`
      }
      return `/moods?mood_meter_group_id=eq.${id}&select=*,emotion_words(*)`
    },
  },
})

export const postMoodService = buildService<NoParams, Mood, Partial<Mood>, Mood>({
  type: 'POST',
  constantsPrefix: 'MOODS',
  http: {
    headers: {
      Prefer: 'return=minimal',
    },
    createApiUrlSuffix: () => '/moods',
  },
})

export const deleteMoodService = buildService<DeleteMoodParams, Mood, unknown, Mood>({
  type: 'DELETE',
  constantsPrefix: 'MOODS',
  http: {
    createApiUrlSuffix: ({ id }) => `/moods?id=eq.${id}`,
    *successHandler(mood: Mood) {
      yield put(changeModal({ isOpen: false }))
      if (getMoodsService.actions.removeItem) {
        yield put(getMoodsService.actions.removeItem({ id: mood.id }))
      }
    },
  },
})

export const patchMoodMeterGroupsService = buildService<
  PatchMoodMeterGroupParams,
  MoodMeterGroup,
  Partial<MoodMeterGroup>,
  MoodMeterGroup
>({
  type: 'PATCH',
  constantsPrefix: 'MOOD_METER_GROUPS',
  http: {
    createApiUrlSuffix: ({ id }) => {
      return `/mood_meter_groups?id=eq.${id}`
    },
  },
})

export const getMyGroupsService = buildService<NoParams, MoodMeterGroup, unknown, MoodMeterGroup>({
  type: 'GET',
  constantsPrefix: 'MY_GROUPS',
  isArray: true,
  http: {
    createApiUrlSuffix: () => `/my_mood_meter_groups`,
  },
})

export const getGroupsSharedWithMeService = buildService<
  NoParams,
  MoodMeterGroup,
  unknown,
  MoodMeterGroup
>({
  type: 'GET',
  constantsPrefix: 'GROUPS_SHARED_WITH_ME',
  isArray: true,
  http: {
    createApiUrlSuffix: () => `/mm_groups_shared_with_me`,
  },
})

export const getMoodMeterGroupsService = buildService<
  GetMoodMeterGroupsParams,
  MoodMeterGroup,
  unknown,
  MoodMeterGroup
>({
  type: 'GET',
  constantsPrefix: 'MOOD_METER_GROUPS',
  isArray: true,
  http: {
    createApiUrlSuffix: ({ id, short_url, owner_id, shared_in, name, order }) => {
      let queryParamsString = '?'
      if (shared_in) {
        queryParamsString += `shared_in=eq.${shared_in}`
        if (shared_in !== SHARED_IN_OPTIONS.personal) {
          return `/mood_meter_groups${queryParamsString}`
        }
      }
      if (id) return `/mood_meter_groups${queryParamsString}&id=eq.${id}`
      if (short_url) return `/mood_meter_groups${queryParamsString}&short_url=eq.${short_url}`
      if (owner_id) return `/mood_meter_groups${queryParamsString}&owner_id=eq.${owner_id}`

      const params = new URLSearchParams()
      params.set('select', 'id,name,short_url,start_time,collected_moods')
      params.set('order', order || 'start_time.desc')
      if (name) {
        params.set('name', postgrestEqFilter(name))
      }

      const listQuery = params.toString()
      return queryParamsString === '?'
        ? `/mood_meter_groups?${listQuery}`
        : `/mood_meter_groups${queryParamsString}&${listQuery}`
    },
  },
})

export const postMoodMeterGroupService = buildService<
  NoParams,
  MoodMeterGroup,
  Partial<MoodMeterGroup>,
  MoodMeterGroup
>({
  type: 'POST',
  constantsPrefix: 'MOOD_METER_GROUPS',
  http: {
    createApiUrlSuffix: () => '/mood_meter_groups',
    *successHandler(data: MoodMeterGroup) {
      yield put(changeModal({ isOpen: false }))
      yield put(resetMoodMeterGroupFormData())

      mixpanel.track(EVENTS.START_MOOD_METER_GROUP, {
        mood_meter_group_id: data.id,
        mood_meter_group_name: data.name,
        mood_meter_short_url: data.short_url,
      })
      Router.push(urls.moodMeterGroupDetails(data.short_url))
    },
    *failureHandler(error: unknown) {
      yield put(
        addGroupFormFieldError({
          field: 'base',
          error: _.get(
            error as Record<string, unknown>,
            'response.data.message',
            lang.unhandledServerError,
          ) as string,
        }),
      )
    },
  },
})

export const deleteMoodMeterGroupsService = buildService<
  DeleteMoodParams,
  MoodMeterGroup,
  unknown,
  MoodMeterGroup
>({
  type: 'DELETE',
  constantsPrefix: 'MOOD_METER_GROUPS',
  http: {
    createApiUrlSuffix: ({ id }) => `/mood_meter_groups?id=eq.${id}`,
    *successHandler() {
      yield put(reloadGroupPlotsAfterDelete())
    },
  },
})

export const closeMoodMeterGroupService = buildService<
  NoParams,
  void,
  { short_code: string },
  void
>({
  type: 'POST',
  constantsPrefix: 'CLOSE_MOOD_METER_GROUP',
  http: {
    createApiUrlSuffix: () => `/rpc/mood_meter_group_close`,
  },
})

function* submitMoodMeterPlot(formData: PlotFormData, groupId?: string) {
  const data: Partial<Mood> = {
    chosen_word_id: formData.chosenEmotionWord,
    reason: formData.reason,
    pleasantness: formData.pleasantness,
    energy: formData.energy,
  }
  if (groupId) {
    data.mood_meter_group_id = groupId
    delete data.reason
  }
  // @ts-expect-error - saga typing
  yield call(postMoodService.requestSaga, {
    payload: {
      data,
    },
  })
}

export function* submitPersonalMoodMeterPlot({ payload }: { payload: { groupId?: string } }) {
  const formData: PlotFormData = yield select((state: AppState) => state.moodMeter.forms.plot.data)
  yield call(submitMoodMeterPlot, formData, payload.groupId)
  // Track the personal mood meter plot event
  mixpanel.track(EVENTS.PLOT_PERSONAL_MOOD_METER, {
    is_personal: true,
    pleasantness: formData.pleasantness,
    energy: formData.energy,
    emotion_word_id: formData.chosenEmotionWord,
    reason: formData.reason,
  })
  yield put(
    changeModal({
      isOpen: true,
      hideCloseButton: true,
      shouldCloseOnOverlayClick: false,
      aria: { labelledby: 'successful-completion-header' },
      onClose: () => {
        window.location.href = urls.moodMeter()
      },
      className: 'successful-completion',
      content: <SuccessfulCompletionModal />,
    }),
  )
}

export function* submitGroupMoodMeterPlot({ payload }: { payload: { groupId?: string } }) {
  const formData: PlotFormData = yield select((state: AppState) => state.moodMeter.forms.plot.data)
  yield call(submitMoodMeterPlot, formData, payload.groupId)
  const group: MoodMeterGroup = yield select((state: AppState) => state.moodMeter.group.item)
  const { short_url } = group
  mixpanel.track(EVENTS.PLOT_MOOD_METER, {
    mood_meter_group_id: group.id,
    mood_meter_group_name: group.name,
    mood_meter_short_url: short_url,
  })
  Router.push(urls.moodMeterPlotInGroupSuccess(short_url))
}

export function* validateMoodMeterGroupFormFieldSaga({
  payload: { field, value, validations },
}: {
  payload: { field: string; value: string; validations: string }
}) {
  const error = validateField({ field, value, fieldValidations: validations })

  if (error) {
    yield put(addGroupFormFieldError({ field, error: error as string }))
  } else {
    yield put(clearGroupFormFieldError({ field }))
  }
}

export function* startGroupMoodMeter({ payload }: { payload: StartMoodMeterGroupPlotPayload }) {
  const errors = validateFields(
    payload as unknown as Record<string, { value: unknown; validations?: string }>,
  )
  if (_.isEmpty(errors)) {
    const data: Partial<MoodMeterGroup> = {
      name: payload.groupName.value as string,
      audience_type_id: payload.audienceType.value as string,
      duration: payload.duration.value as string,
      shared_in: payload.sharedIn.value as string,
    }

    if (_.isDate(payload.startTime.value)) {
      data.start_time = (payload.startTime.value as Date).toISOString()
    }

    yield put(
      postMoodMeterGroupService.actions.request({
        data,
      }),
    )
  } else {
    yield put(addGroupFormFieldsErrors(errors as Record<string, string>))
  }
}

function* loadMoodMeterGroupPageSaga({
  payload: { req, shortCode },
}: {
  payload: { req?: unknown; shortCode: string }
}) {
  // @ts-expect-error - saga typing
  yield call(getMoodMeterGroupByShortCodeService.requestSaga, {
    payload: {
      req,
      data: { short_code: shortCode },
    },
  })
  // @ts-expect-error - requestSuccess typing
  yield put(getMoodsService.actions.requestSuccess([]))
}

export default function* Saga() {
  yield all([
    // @ts-expect-error - saga typing
    takeLatest(SUBMIT_GROUP_MOOD_METER_PLOT, submitGroupMoodMeterPlot),
    // @ts-expect-error - saga typing
    takeLatest(SUBMIT_PERSONAL_MOOD_METER_PLOT, submitPersonalMoodMeterPlot),
    // @ts-expect-error - saga typing
    takeLatest(VALIDATE_MOOD_METER_GROUP_FORM_FIELD, validateMoodMeterGroupFormFieldSaga),
    // @ts-expect-error - saga typing
    takeLatest(START_MOOD_METER_GROUP_PLOT, startGroupMoodMeter),
    // @ts-expect-error - saga typing
    takeLatest(LOAD_MOOD_METER_GROUP_PAGE, loadMoodMeterGroupPageSaga),
    fork(postMoodService.saga),
    fork(getMoodsService.saga),
    fork(deleteMoodService.saga),
    fork(getMoodMeterGroupsService.saga),
    fork(getMoodMeterGroupByShortCodeService.saga),
    fork(postMoodMeterGroupService.saga),
    fork(patchMoodMeterGroupsService.saga),
    fork(deleteMoodMeterGroupsService.saga),
    fork(getMyGroupsService.saga),
    fork(getGroupsSharedWithMeService.saga),
    fork(closeMoodMeterGroupService.saga),
    fork(groupPlotsFilterSaga),
  ])
}
